"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button, Container, Text, Alert } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { logErrorBoundary, getUserMessage } from "@/lib/client/errors";
import styles from "./index.module.scss";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to Sentry and console
    logErrorBoundary(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const error = this.state.error;
      const userMessage = error ? getUserMessage(error) : "Something went wrong.";

      return (
        <Container size="sm" py="xl" className={styles.container}>
          <Alert
            icon={<IconAlertCircle size={20} />}
            title="Oops! Something went wrong"
            color="red"
            variant="light"
            className={styles.alert}
          >
            <Text size="lg" mb="md">
              {userMessage}
            </Text>

            {this.props.showDetails && error && (
              <Text size="sm" c="dimmed" mb="md">
                Error: {error.message}
              </Text>
            )}

            <Button onClick={this.handleReset} variant="filled">
              Try Again
            </Button>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

// Functional wrapper for convenience
interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
}

export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryWrapperProps, "children">
): React.ComponentType<P> {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

// HOC for class components
export function withErrorBoundaryHOC<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryWrapperProps, "children">
): React.ComponentType<P> {
  return class WithErrorBoundary extends Component<P> {
    static displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;

    render(): ReactNode {
      return (
        <ErrorBoundary {...errorBoundaryProps}>
          <WrappedComponent {...this.props} />
        </ErrorBoundary>
      );
    }
  };
}
